<script setup lang="ts">
// An SRT or RIST link's figures (DetailPanel) as its end sees them: an input
// receives, a destination sends; and an IP input's MPEG-TS errors. From
// libsrt through ffmpeg-ese.patch (ffcmd/linkstats.go), once a second.
// Lost packets are normal on the internet while SRT resends them in time;
// only those lost for good reach a viewer. Next to them what the kernel
// says of its sockets (node/sockets.go): a TCP connection (RTMP and other
// TCP sources, RTMP push) and the UDP datagrams dropped before they were
// read.
import { computed } from 'vue'
import type { SocketStats, SRTStats, TSErrors } from '../../api'
import { bytes } from '../../util'

const props = defineProps<{ srt?: SRTStats; ts?: TSErrors; sock?: SocketStats; sending?: boolean }>()
const tcp = computed(() => props.sock?.tcp)
const tcpPct = computed(() => {
    const t = tcp.value
    if (!t?.retransmitted || !t.segments) return '0'
    const p = (t.retransmitted * 100) / t.segments
    return p >= 10 ? p.toFixed(0) : p >= 0.1 ? p.toFixed(1) : '<0.1'
})
// a send queue of more than 2 s of the stream: the link does not keep up (node: queueHigh)
const queueLong = computed(() => (tcp.value?.queue_seconds ?? 0) > 2)
const udpLost = computed(() => (props.sock?.udp_drops ?? 0) > 0 || (props.sock?.overruns ?? 0) > 0)
const pct = (n: number) => {
    const p = props.srt && props.srt.packets > 0 ? (n * 100) / props.srt.packets : 0
    return p >= 10 ? p.toFixed(0) : p >= 0.1 ? p.toFixed(1) : p > 0 ? '<0.1' : '0'
}
const mbit = (v: number) => (v >= 100 ? v.toFixed(0) : v.toFixed(1)) + ' Mb/s'
const count = (n: number) => n.toLocaleString()
const rist = computed(() => props.srt?.protocol === 'rist')
// the buffer near empty while data flows: packets arrive just in time
const tight = computed(
    () => !!props.srt && !props.sending && props.srt.latency_ms > 0 && props.srt.buffer_ms < props.srt.latency_ms / 4,
)
</script>

<template>
    <div class="mt-3 rounded-md border border-line px-2.5 py-2 text-xs">
        <template v-if="srt">
            <div class="mb-1 flex flex-wrap items-baseline gap-x-2">
                <b class="font-semibold">{{ rist ? 'RIST' : 'SRT' }} link</b>
                <span class="text-fg-2">{{ sending ? 'sending' : 'receiving' }} · {{ mbit(srt.rate_mbps) }}</span>
            </div>
            <div class="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-x-3.5 gap-y-1.5">
                <div>
                    <div class="text-[11px] text-fg-3">round trip</div>
                    <div class="font-mono text-[13px] tabular-nums">{{ srt.rtt_ms.toFixed(1) }} ms</div>
                </div>
                <div v-if="rist" title="librist's, the last second: packets that arrived, of those sent">
                    <div class="text-[11px] text-fg-3">quality</div>
                    <div
                        class="font-mono text-[13px] tabular-nums"
                        :class="(srt.quality ?? 100) < 99 ? 'text-warn' : ''"
                    >
                        {{ (srt.quality ?? 0).toFixed(1) }} %
                    </div>
                </div>
                <div v-if="!rist" title="SRT's estimate of what the path between the two ends carries">
                    <div class="text-[11px] text-fg-3">link capacity</div>
                    <div class="font-mono text-[13px] tabular-nums">
                        {{ srt.link_mbps ? mbit(srt.link_mbps) : '—' }}
                    </div>
                </div>
                <div
                    v-if="!rist"
                    :title="
                        sending
                            ? 'sent and not yet acknowledged, of the latency both ends agreed'
                            : 'waiting to be played, of the latency both ends agreed: near empty means packets arrive just in time'
                    "
                >
                    <div class="text-[11px] text-fg-3">buffer · latency</div>
                    <div class="font-mono text-[13px] tabular-nums" :class="tight ? 'text-warn' : ''">
                        {{ srt.buffer_ms }} · {{ srt.latency_ms }} ms
                    </div>
                </div>
                <div
                    :title="
                        rist
                            ? 'packets that went missing on the way, since the input started; RIST asks for them again'
                            : 'packets that went missing on the way, since the connection began; SRT resends them'
                    "
                >
                    <div class="text-[11px] text-fg-3">lost</div>
                    <div class="font-mono text-[13px] tabular-nums">
                        {{ count(srt.lost) }} <span class="text-fg-2">· {{ pct(srt.lost) }} %</span>
                    </div>
                </div>
                <div title="packets sent again">
                    <div class="text-[11px] text-fg-3">{{ sending ? 'resent' : 'resent to it' }}</div>
                    <div class="font-mono text-[13px] tabular-nums">{{ count(srt.retransmitted) }}</div>
                </div>
                <div
                    :title="
                        rist
                            ? 'packets given up on: not recovered within the buffer. The only losses a viewer sees; raise the recovery buffer, or the link is too slow'
                            : 'packets given up on: they could not be resent within the latency. The only losses a viewer sees; raise the latency, or the link is too slow'
                    "
                >
                    <div class="text-[11px] text-fg-3">lost for good</div>
                    <div class="font-mono text-[13px] tabular-nums" :class="srt.dropped ? 'text-bad' : ''">
                        {{ count(srt.dropped) }}
                    </div>
                </div>
                <div v-if="sending && srt.in_flight">
                    <div class="text-[11px] text-fg-3">in flight</div>
                    <div class="font-mono text-[13px] tabular-nums">{{ count(srt.in_flight) }} packets</div>
                </div>
                <div v-if="srt.undecrypted" title="packets that could not be decrypted: the passphrases do not match">
                    <div class="text-[11px] text-fg-3">not decrypted</div>
                    <div class="font-mono text-[13px] text-bad tabular-nums">{{ count(srt.undecrypted) }}</div>
                </div>
            </div>
        </template>
        <template v-if="tcp">
            <div class="mb-1 flex flex-wrap items-baseline gap-x-2" :class="srt ? 'mt-2' : ''">
                <b class="font-semibold">TCP connection</b>
                <span class="text-fg-2"
                    >{{ sending ? 'to' : 'from' }} <span class="font-mono">{{ tcp.peer }}</span> ·
                    {{ mbit(tcp.rate_mbps) }}</span
                >
            </div>
            <div class="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-x-3.5 gap-y-1.5">
                <div title="the kernel's smoothed round trip, and the lowest it saw">
                    <div class="text-[11px] text-fg-3">round trip · lowest</div>
                    <div class="font-mono text-[13px] tabular-nums">
                        {{ tcp.rtt_ms.toFixed(1) }} · {{ tcp.min_rtt_ms.toFixed(1) }} ms
                    </div>
                </div>
                <div v-if="sending" title="segments sent again since the connection began: lost on the way">
                    <div class="text-[11px] text-fg-3">resent</div>
                    <div class="font-mono text-[13px] tabular-nums">
                        {{ count(tcp.retransmitted ?? 0) }} <span class="text-fg-2">· {{ tcpPct }} %</span>
                    </div>
                </div>
                <div
                    v-if="sending"
                    title="in the kernel, not yet acknowledged by the other end; growing means the link does not keep up with the stream"
                >
                    <div class="text-[11px] text-fg-3">send queue</div>
                    <div class="font-mono text-[13px] tabular-nums" :class="queueLong ? 'text-warn' : ''">
                        {{ bytes(tcp.queue_bytes ?? 0) }}
                        <span v-if="tcp.queue_seconds" class="text-fg-2">· {{ tcp.queue_seconds.toFixed(1) }} s</span>
                    </div>
                </div>
            </div>
        </template>
        <div v-if="sock?.udp_drops || sock?.overruns" :class="srt || tcp ? 'mt-1.5' : ''">
            <b class="font-semibold">UDP</b>
            <span class="font-mono tabular-nums" :class="udpLost && !srt ? 'text-warn' : 'text-fg-2'">
                · {{ count(sock?.udp_drops ?? 0) }} dropped by the kernel</span
            ><span v-if="sock?.overruns" class="font-mono text-warn tabular-nums">
                · {{ count(sock.overruns) }} ffmpeg buffer overruns</span
            >
            <span class="text-fg-3">
                since it started ({{
                    srt ? 'SRT resends them' : 'lost: raise net.core.rmem_max, or the node is too busy'
                }})</span
            >
        </div>
        <div v-if="ts" :class="srt || tcp || sock?.udp_drops ? 'mt-1.5' : ''">
            <b class="font-semibold">MPEG-TS errors</b>
            <span class="font-mono tabular-nums" :class="ts.continuity || ts.tei ? 'text-warn' : 'text-fg-2'">
                · {{ count(ts.continuity) }} continuity (1.4)</span
            ><span v-if="ts.tei" class="font-mono text-warn tabular-nums">
                · {{ count(ts.tei) }} marked damaged (2.1)</span
            >
            <span class="text-fg-3"> since the input started (packets lost before the node read them)</span>
        </div>
    </div>
</template>
